import type { FinancialYear } from "../types.js";
import type { IncomeComponents } from "../income-tests/index.js";
import { deriveAu13FamilyIncome } from "./derive-income.js";
import { fy2025_26 } from "./fy2025-26.js";
import type { Au13Input, Au13RuleSet } from "./types.js";

// ============================================================================
//  AU-13 CCS ruleset for FY2026-27 (1 July 2026 – 30 June 2027).
//
//  ⚠️⚠️  DATA GATE — DO NOT SHIP TO USERS  ⚠️⚠️
//
//  This ruleset exists to wire AU-13's family income through the shared
//  `income-tests` engine (Item 1 follow-on): the combined family income is
//  DERIVED as Adjusted Taxable Income (ATI) from raw components via
//  `adjustedTaxableIncome`, not passed in pre-derived. That income-derivation
//  wiring is REAL and tested (incl. the 0.53 exempt-only RFBA un-gross-up).
//
//  HOWEVER, every CCS THRESHOLD below — the §1.1.A.70 applicable-percentage
//  taper, the higher-second-child schedule, and the §3.5.3 hourly rate caps —
//  is a PLACEHOLDER copied from the shipped FY2025-26 ruleset. The CCS income
//  thresholds and rate caps are indexed annually (CPI) and the FY2026-27
//  figures may not be published yet. We do NOT invent them (regulatory
//  guardrail: every output cites a real source).
//
//  Until the real FY2026-27 figures land, this ruleset MUST NOT back a live
//  user-facing calculation. The `FY2026_27_THRESHOLDS_ARE_PLACEHOLDER` flag and
//  the `placeholderThresholds` field exist so callers can refuse to serve it.
//  It is intentionally NOT registered in au13/index.ts's AU13_RULESETS /
//  AU13_CURRENT_FY (so `au13RulesFor("2026-27")` still throws) until the data
//  gate is cleared.
//
//  TODO(data): replace the placeholder standardTaper / higherRate / rateCaps
//  with the published DSS/Services Australia FY2026-27 figures, repoint
//  `sources`, update `sourcesVerifiedAt`, flip `placeholderThresholds` to false,
//  drop the PLACEHOLDER flag, and register this ruleset in au13/index.ts.
//  Sources to watch: §1.1.A.70 (percentage), §3.5.3 (rate caps).
// ============================================================================

/** FY this placeholder ruleset is authored for. */
export const FY2026_27: FinancialYear = "2026-27";

/**
 * LOUD data-gate flag. While `true`, the FY2026-27 CCS thresholds/caps are a
 * copy of FY2025-26 and the ruleset must NOT serve live user calculations.
 * Callers (API layer) should assert this is `false` before using `fy2026_27`.
 */
export const FY2026_27_THRESHOLDS_ARE_PLACEHOLDER = true;

export const fy2026_27: Au13RuleSet & { placeholderThresholds: boolean } = {
  product: "AU13",
  fy: "2026-27",
  effectiveFrom: "2026-07-01",
  effectiveTo: "2027-06-30",
  periodKey: "2026-27",
  periodLabel: "FY2026-27 (3 Day Guarantee era) — PLACEHOLDER thresholds",
  periodFrom: "2026-07-01",
  periodTo: "2027-06-30",
  // TODO(data): point at the FY2026-27 DSS guide revisions once published.
  // These still reference the FY2025-26 pages because the figures below are the
  // FY2025-26 placeholder copy — NOT verified FY2026-27 data.
  sources: { ...fy2025_26.sources },
  // NOT a verification of FY2026-27 figures — these are placeholders.
  sourcesVerifiedAt: "PLACEHOLDER — FY2026-27 CCS thresholds not yet published",

  // PLACEHOLDER: copied verbatim from the shipped FY2025-26 ruleset.
  // TODO(data): replace all three with published FY2026-27 figures.
  standardTaper: { ...fy2025_26.standardTaper },
  higherRate:
    fy2025_26.higherRate === null ? null : { ...fy2025_26.higherRate },
  // Hours rules (3 Day Guarantee: 72/100, indigenous 100) are structural, not
  // CPI-indexed threshold data, so reusing them is safe pre-data-gate.
  hours: { ...fy2025_26.hours, activityTiers: [...fy2025_26.hours.activityTiers] },
  // PLACEHOLDER rate caps. TODO(data): replace with FY2026-27 §3.5.3 caps.
  rateCaps: { ...fy2025_26.rateCaps },

  /** True while the taper/higher-rate/rate-cap figures above are placeholders. */
  placeholderThresholds: FY2026_27_THRESHOLDS_ARE_PLACEHOLDER,
};

/**
 * Derive the COMBINED family ATI for FY2026-27 from raw components, via the
 * shared income-tests engine. This is the Item-1 wiring: the income fed to
 * `calculateAu13` is computed from components, not passed pre-derived. The ATI
 * derivation IS FY-aware (the 0.53 exempt-employer factor = 1 − FBT rate), so
 * it resolves against the FY2026-27 income-tests ruleset once registered; only
 * the CCS thresholds/caps above are gated by the placeholder flag.
 */
export function deriveFy2026_27FamilyIncome(args: {
  self: IncomeComponents;
  partner?: IncomeComponents;
}): number {
  return deriveAu13FamilyIncome({ ...args, assessmentYear: FY2026_27 });
}

/** Convenience: a `calculateAu13` input from raw components for FY2026-27. */
export type Au13Fy2026_27ComponentInput = {
  self: IncomeComponents;
  partner?: IncomeComponents;
} & Omit<Au13Input, "familyIncome">;
